/**
 * JWT token parsing and validation utilities
 * Shared across member and admin portals
 */

export interface JwtPayload {
  sub: string
  email?: string
  name?: string
  'cognito:groups'?: string[]
  'cognito:username'?: string
  exp: number
  iat: number
  [key: string]: unknown
}

/**
 * Parse a JWT token and extract the payload
 * @param token - The JWT token string
 * @returns The parsed payload or null if parsing fails
 */
export function parseJwtToken(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) {
      return null
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )

    return JSON.parse(jsonPayload) as JwtPayload
  } catch (error) {
    console.error('Failed to parse JWT token:', error)
    return null
  }
}

/**
 * Check if a JWT token is expired
 * @param token - The JWT token string or parsed payload
 * @returns true if the token is expired, false otherwise
 */
export function isTokenExpired(token: string | JwtPayload): boolean {
  const payload = typeof token === 'string' ? parseJwtToken(token) : token

  if (!payload || !payload.exp) {
    return true
  }

  // Check if token is expired (with 60 second buffer for clock skew)
  const expirationTime = payload.exp * 1000 // Convert to milliseconds
  const currentTime = Date.now()
  const buffer = 60 * 1000 // 60 seconds

  return currentTime >= expirationTime - buffer
}

/**
 * Extract username from JWT token
 * Tries cognito:username first, falls back to sub
 */
export function extractUsername(payload: JwtPayload): string {
  return payload['cognito:username'] || payload.sub
}

/**
 * Extract user groups from JWT token payload
 */
export function extractGroups(payload: JwtPayload): string[] {
  return payload['cognito:groups'] || []
}
